var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useCurrentAccount, useWallets, useDAppKit } from '@mysten/dapp-kit-react';
import { useAccounts, useSignAndExecuteTransaction } from '../hooks/walletCompat';
import { AlphalendClient } from 'alphalend-sdk';
import { getSuiGraphqlUrl, getSuiGrpcOptions } from '../utils/rpcEndpoints';
import { useLocation } from 'react-router-dom';
import { useAdminAddress } from '../context/AdminAddressContext';
import { getIcon } from '../utils/getIcon';
import { GlobalWalletModal, GlobalConnectWalletButton, GlobalWalletDropdown, NotificationComp, } from 'ui-kit';
import { usePosition } from '../context/PositionContext';
import { useSuinsNames } from '../hooks/useSuinsName';
import { useNetworkString } from '../context/NetworkContext';
import { useMultiWalletPositions } from '../utils/positionUtils';
import { getSuiClient } from '../utils/client';
import { createNewPosition, createPositionNotificationInfo, } from '../utils/newPositionHandler';
import { useBalances } from '../context/BalanceContext';
const adaptWalletForModal = (wallet) => {
    try {
        return {
            name: wallet.name,
            accounts: (wallet.accounts || []),
        };
    }
    catch (error) {
        console.error('Error adapting wallet:', error);
        return { name: 'Unknown Wallet', accounts: [] };
    }
};
const debug = (message) => {
    console.log(message);
};
const WalletContent = (props) => {
    const { currentAccount, onConnectionChange } = props;
    const dAppKit = useDAppKit();
    const accounts = useAccounts();
    const wallets = useWallets();
    const location = useLocation();
    const network = useNetworkString();
    const { walletBalancesMap } = useBalances();
    const [showDisconnectPopup, setShowDisconnectPopup] = useState(false);
    const [showWalletModal, setShowWalletModal] = useState(false);
    const [shortAddress, setShortAddress] = useState('connect wallet');
    const [connectedWalletType, setConnectedWalletType] = useState(null);
    const [availableWallets, setAvailableWallets] = useState([]);
    const [isCreatingPosition, setIsCreatingPosition] = useState(false);
    const [isNotificationOpen, setIsNotificationOpen] = useState(false);
    const [notificationInfo, setNotificationInfo] = useState({
        title: '',
        description: '',
        link: '',
        image: null,
        spinner: false,
    });
    const signAndExecuteTransaction = useSignAndExecuteTransaction();
    // Get position data from context
    const { selectedPositionCapId, allPositionCapIds, selectPosition, addPositionId, getAllPositionsSummary, clearPositionData, } = usePosition();
    const positionsSummary = useMemo(() => getAllPositionsSummary(allPositionCapIds), 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getAllPositionsSummary, allPositionCapIds.join(',')]);
    const walletPopUpRef = useRef(null);
    const walletButtonRef = useRef(null);
    const mobileDrawerRef = useRef(null);
    const currentPage = useMemo(() => {
        const path = location.pathname;
        if (path === '/')
            return 'Market';
        if (path.startsWith('/market/'))
            return 'MarketDetails';
        if (path === '/portfolio')
            return 'AccountPage';
        return 'Other';
    }, [location.pathname]);
    // Stable reference — only changes when the actual set of addresses changes
    const accountAddresses = useMemo(() => accounts.map((acc) => acc.address), 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [accounts.map((a) => a.address).join(',')]);
    const { suinsNames } = useSuinsNames(accountAddresses);
    // Use useMultiWalletPositions hook to manage position counts, statuses, and position data for all wallets
    const { walletPositionCounts, walletStatusMap, otherWalletPositions, refreshOtherWalletPositions, } = useMultiWalletPositions(accountAddresses, currentAccount, allPositionCapIds, positionsSummary, network);
    // Build walletPositions map: current account uses live positionsSummary; others use fetched data
    const walletPositions = useMemo(() => (Object.assign(Object.assign({}, (currentAccount
        ? {
            [currentAccount]: positionsSummary.map((summary, index) => ({
                id: summary.id,
                label: `Position ${index + 1}`,
                netWorth: summary.netWorth,
                totalSupplied: summary.totalSupplied,
                totalBorrowed: summary.totalBorrowed,
                borrowLimitUsedPercentage: summary.borrowLimitUsedPercentage,
                safeLimitPercentage: summary.safeLimitPercentage,
                liquidationPercentage: summary.liquidationPercentage,
            })),
        }
        : {})), Object.fromEntries(Object.entries(otherWalletPositions).map(([address, summaries]) => [
        address,
        summaries.map((summary, index) => ({
            id: summary.id,
            label: `Position ${index + 1}`,
            netWorth: summary.netWorth,
            totalSupplied: summary.totalSupplied,
            totalBorrowed: summary.totalBorrowed,
            borrowLimitUsedPercentage: summary.borrowLimitUsedPercentage,
            safeLimitPercentage: summary.safeLimitPercentage,
            liquidationPercentage: summary.liquidationPercentage,
        })),
    ])))), [currentAccount, positionsSummary, otherWalletPositions]);
    useEffect(() => {
        try {
            if (!Array.isArray(wallets) || wallets.length === 0) {
                setAvailableWallets([]);
                return;
            }
            const displayWallets = wallets.map((wallet) => {
                const safeWallet = {
                    id: typeof wallet.name === 'string' ? wallet.name.toLowerCase() : 'unknown',
                    name: wallet.name || 'Unknown Wallet',
                    icon: wallet.icon,
                    available: true,
                    connected: connectedWalletType ===
                        (typeof wallet.name === 'string' ? wallet.name.toLowerCase() : 'unknown'),
                    walletAdapter: adaptWalletForModal(wallet),
                };
                return safeWallet;
            });
            setAvailableWallets(displayWallets);
        }
        catch (error) {
            console.error('Error mapping wallets:', error);
            setAvailableWallets([]);
        }
    }, [wallets, connectedWalletType]);
    // Effect 1: UI state + identify — runs only when wallet identity changes
    useEffect(() => {
        try {
            if (currentAccount) {
                const displayName = suinsNames[currentAccount] || getShortAddress(currentAccount);
                setShortAddress(displayName);
                onConnectionChange === null || onConnectionChange === void 0 ? void 0 : onConnectionChange(true);
                let detectedWalletName = null;
                if (Array.isArray(wallets) && wallets.length > 0) {
                    const connectedAdapter = wallets.find((w) => {
                        try {
                            if (!w.accounts || !Array.isArray(w.accounts)) {
                                return false;
                            }
                            return w.accounts.some((acc) => acc && acc.address === currentAccount);
                        }
                        catch (_err) {
                            return false;
                        }
                    });
                    if (connectedAdapter) {
                        detectedWalletName =
                            typeof connectedAdapter.name === 'string'
                                ? connectedAdapter.name.toLowerCase()
                                : null;
                        setConnectedWalletType(detectedWalletName);
                    }
                    else {
                        setConnectedWalletType(null);
                    }
                }
                else {
                    setConnectedWalletType(null);
                }
            }
            else {
                setShortAddress('connect wallet');
                setConnectedWalletType(null);
                onConnectionChange === null || onConnectionChange === void 0 ? void 0 : onConnectionChange(false);
            }
        }
        catch (error) {
            console.error('Error processing wallet connection:', error);
            setShortAddress('connect wallet');
            setConnectedWalletType(null);
        }
    }, [currentAccount, wallets, onConnectionChange, suinsNames, network]);
    useEffect(() => {
        const handleOutsideClick = (event) => {
            const clickedInsidePopup = walletPopUpRef.current && walletPopUpRef.current.contains(event.target);
            const clickedInsideButton = walletButtonRef.current && walletButtonRef.current.contains(event.target);
            const clickedInsideMobileDrawer = mobileDrawerRef.current && mobileDrawerRef.current.contains(event.target);
            if (!clickedInsidePopup && !clickedInsideButton && !clickedInsideMobileDrawer) {
                setShowDisconnectPopup(false);
            }
        };
        document.addEventListener('mousedown', handleOutsideClick);
        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
        };
    }, []);
    const getShortAddress = (address) => {
        if (!address || typeof address !== 'string' || address.length < 8) {
            return address || 'Invalid Address';
        }
        const add1 = address.substring(0, 4);
        const add2 = address.substring(address.length - 4);
        return `${add1}....${add2}`;
    };
    const callDisconnect = () => {
        // Rejects with WalletNotConnectedError if nothing is connected — reachable from a stale
        // popup after the wallet unregistered itself. `void` alone wouldn't catch that, so the
        // rejection is handled explicitly; the UI outcome (closed popup, disconnected) is the
        // same either way. dApp Kit already warns about failures inside the wallet's own
        // disconnect handler.
        void dAppKit.disconnectWallet().catch(() => { });
        setShowDisconnectPopup(false);
    };
    const showCreatePositionNotification = useCallback((status, errorMessage) => {
        const info = createPositionNotificationInfo(status, getIcon, errorMessage);
        setNotificationInfo(info);
        setIsNotificationOpen(true);
    }, []);
    // Create position for address.
    const createPositionForAddress = useCallback((address, signingAccount) => __awaiter(void 0, void 0, void 0, function* () {
        const suiClient = getSuiClient(network);
        const alphalendClient = new AlphalendClient(network, getSuiGraphqlUrl(network), getSuiGrpcOptions(network));
        const suiBalance = (walletBalancesMap === null || walletBalancesMap === void 0 ? void 0 : walletBalancesMap.get('0x2::sui::SUI')) || '0';
        // Sign with a specific wallet account without switching the active account in the UI
        const boundSignAndExecute = signingAccount
            ? (vars, opts) => signAndExecuteTransaction(Object.assign(Object.assign({}, vars), { account: signingAccount }), opts)
            : signAndExecuteTransaction;
        setIsCreatingPosition(true);
        try {
            const outcome = yield createNewPosition({
                network,
                address,
                suiClient,
                alphalendClient,
                signAndExecuteTransaction: boundSignAndExecute,
                suiBalance,
                showNotification: showCreatePositionNotification,
            });
            if (outcome.ok) {
                if (outcome.newPositionCapId) {
                    if (signingAccount) {
                        // Non-current wallet: refresh its positions in the dropdown immediately
                        refreshOtherWalletPositions(address);
                    }
                    else {
                        addPositionId(outcome.newPositionCapId);
                        clearPositionData(outcome.newPositionCapId);
                        selectPosition(outcome.newPositionCapId);
                    }
                }
                showCreatePositionNotification('success');
                document.dispatchEvent(new CustomEvent('transactionSuccess', {
                    detail: { txType: 'create_position', success: true, digest: outcome.digest },
                }));
            }
            else {
                showCreatePositionNotification('error', outcome.errorMessage);
                document.dispatchEvent(new CustomEvent('transactionError', {
                    detail: {
                        txType: 'create_position',
                        success: false,
                        error: outcome.errorMessage,
                        digest: outcome.digest,
                    },
                }));
            }
        }
        finally {
            setIsCreatingPosition(false);
        }
    }), [
        network,
        walletBalancesMap,
        signAndExecuteTransaction,
        showCreatePositionNotification,
        addPositionId,
        refreshOtherWalletPositions,
        selectPosition,
        clearPositionData,
    ]);
    // Creates a new position for the given wallet, or the current wallet if none is specified
    const handleCreateNewPosition = useCallback((targetAccount) => {
        if (targetAccount && targetAccount.address !== currentAccount) {
            // Non-current wallet: full account details needed to sign the transaction without switching wallets
            const fullAccount = accounts.find((a) => (a === null || a === void 0 ? void 0 : a.address) === targetAccount.address);
            if (!fullAccount)
                return;
            void createPositionForAddress(targetAccount.address, fullAccount);
        }
        else {
            // Current wallet: create directly under the active account
            if (!currentAccount)
                return;
            void createPositionForAddress(currentAccount);
        }
    }, [currentAccount, accounts, createPositionForAddress]);
    const handleConnectClick = () => {
        setShowWalletModal(true);
    };
    const handleConnectWallet = (walletAdapter, walletId) => {
        try {
            if (!walletAdapter || !walletAdapter.name) {
                console.error('Invalid wallet adapter');
                return;
            }
            if (!Array.isArray(wallets) || wallets.length === 0) {
                console.error('No wallets available');
                return;
            }
            const originalWallet = wallets.find((w) => {
                try {
                    return w.name === walletAdapter.name;
                }
                catch (_err) {
                    return false;
                }
            });
            if (!originalWallet) {
                console.error('Original wallet not found');
                return;
            }
            dAppKit
                .connectWallet({ wallet: originalWallet })
                .then(() => {
                setShowWalletModal(false);
                setConnectedWalletType(walletId);
            })
                .catch((error) => {
                var _a;
                console.error('Error connecting to wallet:', error);
                setShowWalletModal(false);
                const isMaxConnections = (_a = error === null || error === void 0 ? void 0 : error.message) === null || _a === void 0 ? void 0 : _a.includes('maximum number of dApp connections');
                setNotificationInfo({
                    title: 'Wallet Connection Failed',
                    description: isMaxConnections
                        ? 'You have reached the maximum number of dApp connections (100). Please disconnect some dApps in your wallet settings and try again.'
                        : (error === null || error === void 0 ? void 0 : error.message) || 'Failed to connect wallet. Please try again.',
                    link: '',
                    image: getIcon('failed'),
                    spinner: false,
                });
                setIsNotificationOpen(true);
            });
        }
        catch (error) {
            console.error('Error in handleConnectWallet:', error);
        }
    };
    return (_jsxs(_Fragment, { children: [_jsxs("div", { className: "relative", children: [_jsx("div", { ref: walletButtonRef, children: _jsx(GlobalConnectWalletButton, { currentAccount: currentAccount, shortAddress: shortAddress, onConnectClick: handleConnectClick, onDisconnectClick: callDisconnect, showDisconnectPopup: showDisconnectPopup, setShowDisconnectPopup: setShowDisconnectPopup, buttonText: props.buttonText || 'Connect Wallet', buttonStyle: props.buttonStyle, walletUpArrow: getIcon('walletUpArrow'), walletDownArrow: getIcon('walletDownArrow') }) }), showDisconnectPopup && (_jsx(GlobalWalletDropdown, { accounts: accounts, currentAccount: currentAccount, walletPopUpRef: walletPopUpRef, mobileDrawerRef: mobileDrawerRef, onClose: () => setShowDisconnectPopup(false), suinsNames: suinsNames, onAccountSwitch: (accountBasic) => {
                            try {
                                if (!accountBasic || !accountBasic.address) {
                                    console.error('Invalid account provided');
                                    return;
                                }
                                const fullAccount = accounts.find((a) => {
                                    try {
                                        return a && a.address === accountBasic.address;
                                    }
                                    catch (_err) {
                                        return false;
                                    }
                                });
                                if (fullAccount) {
                                    try {
                                        // v2: switchAccount is synchronous (no mutation callbacks).
                                        dAppKit.switchAccount({ account: fullAccount });
                                        debug(`Switched to ${fullAccount.address}`);
                                        setShowDisconnectPopup(false);
                                    }
                                    catch (error) {
                                        console.error('Error switching account:', error);
                                    }
                                }
                            }
                            catch (error) {
                                console.error('Error in onAccountSwitch:', error);
                            }
                        }, onDisconnect: callDisconnect, onCopyAddress: (address) => {
                            try {
                                if (address && typeof address === 'string') {
                                    navigator.clipboard.writeText(address);
                                }
                            }
                            catch (error) {
                                console.error('Error copying address:', error);
                            }
                        }, onViewExplorer: (address) => {
                            try {
                                if (address && typeof address === 'string') {
                                    // Check if this is a position ID by checking if it exists in allPositionCapIds
                                    const isPositionId = allPositionCapIds.includes(address);
                                    const path = isPositionId ? 'object' : 'account';
                                    window.open(`https://suiscan.xyz/mainnet/${path}/${address}`, '_blank');
                                }
                            }
                            catch (error) {
                                console.error('Error opening explorer:', error);
                            }
                        }, getShortAddress: getShortAddress, disconnectIcon: getIcon('disconnectWalletIcon'), copyIcon: getIcon('greyCopyIcon'), hoverCopyIcon: getIcon('whiteCopyIcon'), explorerIcon: getIcon('greyRedirectIcon'), hoverExplorerIcon: getIcon('externalIconWhite'), positions: positionsSummary.map((summary, index) => ({
                            id: summary.id,
                            label: `Position ${index + 1}`,
                            netWorth: summary.netWorth,
                            totalSupplied: summary.totalSupplied,
                            totalBorrowed: summary.totalBorrowed,
                            borrowLimitUsedPercentage: summary.borrowLimitUsedPercentage,
                            safeLimitPercentage: summary.safeLimitPercentage,
                            liquidationPercentage: summary.liquidationPercentage,
                            onRedirect: () => {
                                try {
                                    selectPosition(summary.id);
                                }
                                catch (error) {
                                    console.error('Error switching position:', error);
                                }
                            },
                        })), walletPositions: walletPositions, currentPosition: selectedPositionCapId || undefined, onPositionSwitch: (positionId) => {
                            try {
                                selectPosition(positionId);
                                debug(`Switched to position ${positionId}`);
                            }
                            catch (error) {
                                console.error('Error switching position:', error);
                            }
                        }, showPositions: allPositionCapIds.length >= 1, currentPage: currentPage, walletPositionCounts: walletPositionCounts, walletStatusMap: walletStatusMap, onCreateNewPosition: currentAccount ? handleCreateNewPosition : undefined, createNewPositionLoading: isCreatingPosition, onWalletAndPositionSwitch: (accountBasic, positionId) => {
                            try {
                                if (!(accountBasic === null || accountBasic === void 0 ? void 0 : accountBasic.address) || !positionId)
                                    return;
                                const fullAccount = accounts.find((a) => (a === null || a === void 0 ? void 0 : a.address) === accountBasic.address);
                                if (!fullAccount)
                                    return;
                                // Save the selected position so it gets auto-selected after switching to the new wallet
                                localStorage.setItem(`last-selected-position-${accountBasic.address}`, positionId);
                                try {
                                    // v2: switchAccount is synchronous (no mutation callbacks).
                                    dAppKit.switchAccount({ account: fullAccount });
                                    debug(`Switched to ${fullAccount.address}, target position ${positionId}`);
                                }
                                catch (error) {
                                    console.error('Error switching wallet for position switch:', error);
                                }
                            }
                            catch (error) {
                                console.error('Error in onWalletAndPositionSwitch:', error);
                            }
                        } }))] }), _jsx(GlobalWalletModal, { isOpen: showWalletModal, onClose: () => setShowWalletModal(false), connectedWalletType: connectedWalletType, displayWallets: availableWallets, onWalletConnect: handleConnectWallet, getShortAddress: getShortAddress, closeIcon: props.theme === 'dark' ? getIcon('darkCrossIcon') : getIcon('close') }), _jsx(NotificationComp, { isOpen: isNotificationOpen, setIsOpen: setIsNotificationOpen, info: notificationInfo })] }));
};
const AdminWalletButton = ({ buttonStyle }) => {
    const { adminAddress } = useAdminAddress();
    const location = useLocation();
    const { selectedPositionCapId, allPositionCapIds, selectPosition, getAllPositionsSummary } = usePosition();
    const [showPositionsPopup, setShowPositionsPopup] = useState(false);
    const walletPopUpRef = useRef(null);
    const walletButtonRef = useRef(null);
    const mobileDrawerRef = useRef(null);
    const address = adminAddress !== null && adminAddress !== void 0 ? adminAddress : '';
    const { suinsNames } = useSuinsNames(address ? [address] : []);
    const positionsSummary = useMemo(() => getAllPositionsSummary(allPositionCapIds), 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getAllPositionsSummary, allPositionCapIds.join(',')]);
    const currentPage = useMemo(() => {
        const path = location.pathname;
        if (path === '/')
            return 'Market';
        if (path.startsWith('/market/'))
            return 'MarketDetails';
        if (path === '/portfolio')
            return 'AccountPage';
        return 'Other';
    }, [location.pathname]);
    const getShortAddress = (addr) => {
        if (!addr || typeof addr !== 'string' || addr.length < 8) {
            return addr || 'Invalid Address';
        }
        return `${addr.substring(0, 4)}....${addr.substring(addr.length - 4)}`;
    };
    // The positions dropdown is only offered on the lend ("/") and portfolio pages.
    const isPositionsDropdownAllowed = currentPage === 'Market' || currentPage === 'AccountPage';
    const hasPositions = allPositionCapIds.length >= 1;
    const canShowPositionsDropdown = hasPositions && isPositionsDropdownAllowed;
    const short = getShortAddress(address);
    // Close the popup when navigating to a page where the dropdown isn't allowed
    useEffect(() => {
        if (!isPositionsDropdownAllowed) {
            setShowPositionsPopup(false);
        }
    }, [isPositionsDropdownAllowed]);
    // Close the positions popup when clicking outside of the button/popup/drawer
    useEffect(() => {
        const handleOutsideClick = (event) => {
            const clickedInsidePopup = walletPopUpRef.current && walletPopUpRef.current.contains(event.target);
            const clickedInsideButton = walletButtonRef.current && walletButtonRef.current.contains(event.target);
            const clickedInsideMobileDrawer = mobileDrawerRef.current && mobileDrawerRef.current.contains(event.target);
            if (!clickedInsidePopup && !clickedInsideButton && !clickedInsideMobileDrawer) {
                setShowPositionsPopup(false);
            }
        };
        document.addEventListener('mousedown', handleOutsideClick);
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, []);
    const exitAdminMode = () => {
        const params = new URLSearchParams(window.location.search);
        params.delete('address');
        const newSearch = params.toString();
        window.location.href = `${window.location.pathname}${newSearch ? `?${newSearch}` : ''}`;
    };
    const defaultStyle = 'text-xs xs410:text-sm xl:text-base px-[12.96px] md:px-6 py-[5.16px] md:py-3 rounded-lg md:rounded-[10px] font-InterMedium theme-bg-alphafi-walletConnect text-white whitespace-nowrap cursor-default flex items-center';
    return (_jsxs("div", { className: "relative", children: [_jsxs("div", { ref: walletButtonRef, className: buttonStyle || defaultStyle, children: [_jsx("span", { children: short }), canShowPositionsDropdown && (_jsx("button", { onClick: (e) => {
                            e.stopPropagation();
                            setShowPositionsPopup((prev) => !prev);
                        }, title: showPositionsPopup ? 'Hide positions' : 'Show positions', className: "flex items-center justify-center cursor-pointer ml-2", children: _jsx("img", { className: "h-3 w-3 md:h-4 md:w-4", src: getIcon(showPositionsPopup ? 'walletUpArrow' : 'walletDownArrow'), alt: "Toggle positions" }) })), _jsx("button", { onClick: exitAdminMode, title: "Exit admin mode", className: "flex items-center justify-center cursor-pointer", children: _jsx("svg", { viewBox: "0 0 12 12", fill: "none", xmlns: "http://www.w3.org/2000/svg", className: "h-2 w-2 md:h-3 md:w-4 ml-2", children: _jsx("path", { d: "M1 1L11 11M11 1L1 11", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round" }) }) })] }), showPositionsPopup && canShowPositionsDropdown && (_jsx(GlobalWalletDropdown, { accounts: [{ address, label: '' }], currentAccount: address, walletPopUpRef: walletPopUpRef, mobileDrawerRef: mobileDrawerRef, onClose: () => setShowPositionsPopup(false), suinsNames: suinsNames, onAccountSwitch: () => { }, onDisconnect: () => { }, showDisconnect: false, onCopyAddress: (addr) => {
                    try {
                        if (addr && typeof addr === 'string') {
                            navigator.clipboard.writeText(addr);
                        }
                    }
                    catch (error) {
                        console.error('Error copying address:', error);
                    }
                }, onViewExplorer: (addr) => {
                    try {
                        if (addr && typeof addr === 'string') {
                            const isPositionId = allPositionCapIds.includes(addr);
                            const path = isPositionId ? 'object' : 'account';
                            window.open(`https://suiscan.xyz/mainnet/${path}/${addr}`, '_blank');
                        }
                    }
                    catch (error) {
                        console.error('Error opening explorer:', error);
                    }
                }, getShortAddress: getShortAddress, disconnectIcon: getIcon('disconnectWalletIcon'), copyIcon: getIcon('greyCopyIcon'), hoverCopyIcon: getIcon('whiteCopyIcon'), explorerIcon: getIcon('greyRedirectIcon'), hoverExplorerIcon: getIcon('externalIconWhite'), positions: positionsSummary.map((summary, index) => ({
                    id: summary.id,
                    label: `Position ${index + 1}`,
                    netWorth: summary.netWorth,
                    totalSupplied: summary.totalSupplied,
                    totalBorrowed: summary.totalBorrowed,
                    borrowLimitUsedPercentage: summary.borrowLimitUsedPercentage,
                    safeLimitPercentage: summary.safeLimitPercentage,
                    liquidationPercentage: summary.liquidationPercentage,
                })), currentPosition: selectedPositionCapId || undefined, onPositionSwitch: (positionId) => {
                    try {
                        selectPosition(positionId);
                    }
                    catch (error) {
                        console.error('Error switching position:', error);
                    }
                }, showPositions: allPositionCapIds.length >= 1, currentPage: currentPage, walletPositionCounts: { [address]: allPositionCapIds.length }, 
                // No "Create Position" button in read-only address-view mode
                onCreateNewPosition: undefined }))] }));
};
const AlphalendConnectWallet = (props) => {
    const currentAccount = useCurrentAccount();
    const { isAdminMode, adminAddress } = useAdminAddress();
    if (isAdminMode && adminAddress) {
        return _jsx(AdminWalletButton, { buttonStyle: props.buttonStyle });
    }
    return (_jsx(WalletContent, Object.assign({}, props, { currentAccount: currentAccount === null || currentAccount === void 0 ? void 0 : currentAccount.address, onConnectionChange: props.onConnectionChange || (() => { }) })));
};
export default AlphalendConnectWallet;
